import { DateRange as MaterialDateRange } from '@angular/material/datepicker';
import { Interval, isValid, parse } from 'date-fns';
import { isDate as fnsIsDate } from 'date-fns/isDate';
import { dateRangeInterfaceToModel } from '../../functions/date-range-interface-to-model.function';
import { intervalToRange } from '../../functions/interval-to-range.function';
import { isInterval } from '../../functions/is-interval.function';
import { isRangeString } from '../../functions/is-range-string.function';
import { isRange } from '../../functions/is-range.function';
import { materialDateRangeToDate } from '../../functions/material-date-range-to-date.function';
import { rangeFromString } from '../../functions/range-from-string.function';
import { DateRange } from '../../models/date-range/date-range.model';
import { PlainRangeInterface } from '../../models/range.interface';
import { TsRangeInterface } from '../../models/ts-range/ts-range.interface';
import { TsRange } from '../../models/ts-range/ts-range.model';

// eslint-disable-next-line max-lines-per-function
export function parseRawDate<
  PlainType extends string | number,
  D extends
    | PlainRangeInterface<StartBoundary, EndBoundary>
    | TsRangeInterface<StartBoundary, EndBoundary>
    | TsRange<StartBoundary, EndBoundary>
    | DateRange<StartBoundary, EndBoundary>,
  StartBoundary extends 'finite' | 'infinite' = D['start'] extends null
    ? 'infinite'
    : D['start'] extends PlainType
      ? 'finite'
      : 'infinite',
  EndBoundary extends 'finite' | 'infinite' = D['end'] extends null
    ? 'infinite'
    : D['end'] extends PlainType
      ? 'finite'
      : 'infinite',
>(
  value: MaterialDateRange<Date | string> | Interval | D | string | Date,
  originDateFormat?: string,
  referenceDate?: Date | string,
): TsRange<StartBoundary, EndBoundary> | DateRange<StartBoundary, EndBoundary> | Date | null {
  if (value instanceof DateRange || value instanceof TsRange) {
    return value;
  }

  if (value instanceof MaterialDateRange) {
    return materialDateRangeToDate(value);
  }

  if (isInterval(value)) {
    return dateRangeInterfaceToModel(intervalToRange(value));
  }

  if (isRange(value)) {
    return dateRangeInterfaceToModel(
      value as PlainRangeInterface<StartBoundary, EndBoundary, PlainType>,
    ) as DateRange;
  }

  if (fnsIsDate(value)) {
    return value;
  }

  if (isRangeString(value)) {
    return dateRangeInterfaceToModel(rangeFromString(value));
  }

  const parseDateAttempt = new Date(value);

  if (isValid(parseDateAttempt)) {
    return parseDateAttempt;
  }

  if (originDateFormat) {
    return parse(value, originDateFormat, referenceDate ?? new Date());
  }

  throw new Error('Unable to parse the given input into a exceptable date');
}
